import { Platform, Alert, Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { version as appVersion } from '../../app.json';

class SmartUpdateService {
  constructor() {
    this.packageName = 'com.DynamicDivas';
    this.playStoreUrl = `https://play.google.com/store/apps/details?id=${this.packageName}`;
    this.lastCheckKey = 'lastUpdateCheck';
    this.currentVersionKey = 'currentAppVersion';
    this.configUrl = 'https://dynamicdivas.in/info.json';
    
    // Default fallback values
    this.checkInterval = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.latestVersion = '1.4.1'; // Fallback version
    
    // Get version dynamically from app.json
    this.appVersion = appVersion; // This will always be the current app version
    
  // Load configuration from server
  this.loadServerConfig();
}

// Fetch configuration from server
async loadServerConfig() {
  // Skip on web - updates are handled by browser/server
  if (Platform.OS === 'web') {
    console.log('Skipping server config fetch on web');
    return;
  }

  try {
    console.log('Fetching configuration from server...');
    const response = await fetch(this.configUrl);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const config = await response.json();
    
    if (config.android) {
      this.latestVersion = config.android.latestVersion || this.latestVersion;
      this.checkInterval = config.android.checkIntervalInMilliseconds || this.checkInterval;
      
      if (config.android.PlayStoreUrl) {
        this.playStoreUrl = config.android.PlayStoreUrl;
      }
      
      console.log(`Server config loaded - Latest version: ${this.latestVersion}, Check interval: ${this.checkInterval}ms`);
    }
  } catch (error) {
    // Silently fail on web or if CORS blocks the request
    if (Platform.OS === 'web') {
      console.log('Skipping server config fetch on web');
    } else {
    console.error('Failed to load server configuration, using fallback values:', error.message);
    console.log(`Using fallback - Latest version: ${this.latestVersion}, Check interval: ${this.checkInterval}ms`);
    }
  }
}

// Get current app version from device storage or app.json
  async getCurrentAppVersion() {
    try {
      const storedVersion = await AsyncStorage.getItem(this.currentVersionKey);
      
      // Always use the current app version from app.json as the source of truth
      // This ensures that after a Play Store update, we detect the new version
      if (storedVersion !== this.appVersion) {
        // App was updated (either via Play Store or development), update the stored version
        await AsyncStorage.setItem(this.currentVersionKey, this.appVersion);
        console.log(`App version updated from ${storedVersion || 'unknown'} to ${this.appVersion}`);
      }
      
      // Always return the current app version from app.json
      return this.appVersion;
    } catch (error) {
      console.error('Error getting app version:', error);
      return this.appVersion; // Fallback to app.json version
    }
  }

  // Check if enough time has passed since last check
  async shouldCheckForUpdate() {
    try {
      const lastCheck = await AsyncStorage.getItem(this.lastCheckKey);
      if (!lastCheck) return true;
      
      const now = new Date().getTime();
      const timeSinceLastCheck = now - parseInt(lastCheck);
      
      return timeSinceLastCheck >= this.checkInterval;
    } catch (error) {
      console.error('Error checking update frequency:', error);
      return true;
    }
  }

  // Update last check time
  async updateLastCheckTime() {
    try {
      await AsyncStorage.setItem(this.lastCheckKey, new Date().getTime().toString());
    } catch (error) {
      console.error('Error updating last check time:', error);
    }
  }

  // Smart update check - compares current version with latest version
  async checkForUpdate() {
    try {
      if (Platform.OS !== 'android') {
        console.log('Update checking only supported on Android');
        return { available: false, reason: 'iOS not supported' };
      }

      const shouldCheck = await this.shouldCheckForUpdate();
      if (!shouldCheck) {
        console.log('Skipping update check - too soon since last check');                
        return { available: false, reason: 'Too soon since last check' };
      }

      console.log('Checking for updates...');
      
      // Refresh server configuration to get latest version
      await this.loadServerConfig();
      
      // Get current version from device
      const currentVersion = await this.getCurrentAppVersion();
      
      // Simple version comparison
      const hasUpdate = this.compareVersions(this.latestVersion, currentVersion) > 0;
      
      await this.updateLastCheckTime();
      
      return {
        available: hasUpdate,
        reason: hasUpdate ? 'Update available' : 'No updates available',
        storeVersion: this.latestVersion,
        currentVersion: currentVersion
      };
    } catch (error) {
      console.error('Error checking for updates:', error);
      return { 
        available: false, 
        error: error.message,
        reason: 'Check failed'
      };
    }
  }

  // Compare version strings (e.g., "1.0.1" vs "1.0.0")
  compareVersions(version1, version2) {
    const v1parts = version1.split('.').map(Number);
    const v2parts = version2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(v1parts.length, v2parts.length); i++) {
      const v1part = v1parts[i] || 0;
      const v2part = v2parts[i] || 0;
      
      if (v1part > v2part) return 1;
      if (v1part < v2part) return -1;
    }
    
    return 0;
  }

  // Show update available modal
  async showUpdateModal() {
    const currentVersion = await this.getCurrentAppVersion();
    Alert.alert(
      'Update Available',
      `A new version (${this.latestVersion}) is available! You are currently using version ${currentVersion}.`,
      [
        {
          text: 'Update Now',
          onPress: () => this.openPlayStore()
        },
        {
          text: 'Later',
          style: 'cancel'
        }
      ]
    );
  }

  // Open Play Store
  openPlayStore() {
    const playStoreUrl = `apps://details?id=${this.packageName}`;
    const webUrl = `https://play.google.com/store/apps/details?id=${this.packageName}`;
    
    Linking.openURL(playStoreUrl).catch(() => {
      Linking.openURL(webUrl);
    });
  }

  // Manual update check (for settings screen)
  async manualUpdateCheck() {
    try {
      const result = await this.checkForUpdate();
      
      if (result.available) {
        this.showUpdateModal();
        return { success: true, message: 'Update available!' };
      } else {
        Alert.alert('No Updates', `You are using the latest version (${result.currentVersion}).`);
        return { success: true, message: 'No updates available' };
      }
    } catch (error) {
      console.error('Error in manual update check:', error);
      Alert.alert('Error', 'Failed to check for updates.');
      return { success: false, error: error.message };
    }
  }

  // Get app version info
  async getAppVersion() {
    const currentVersion = await this.getCurrentAppVersion();
    return {
      currentVersion: currentVersion,
      latestVersion: this.latestVersion,
      packageName: this.packageName
    };
  }

  // Method to update latest version when you release new versions
  setLatestVersion(version) {
    this.latestVersion = version;
    console.log(`Latest version updated to: ${version}`);
  }

  // Method to update app version (call this when you update your app.json)
  setAppVersion(version) {
    this.appVersion = version;
    console.log(`App version updated to: ${version}`);
  }

  // Method to check if app was recently updated (useful for showing update success message)
  async wasAppRecentlyUpdated() {
    try {
      const storedVersion = await AsyncStorage.getItem(this.currentVersionKey);
      return storedVersion !== this.appVersion;
    } catch (error) {
      console.error('Error checking if app was recently updated:', error);
      return false;
    }
  }

  // Method to mark app as successfully updated (call this after confirming update)
  async markAppAsUpdated() {
    try {
      await AsyncStorage.setItem(this.currentVersionKey, this.appVersion);
      console.log(`App marked as updated to version: ${this.appVersion}`);
    } catch (error) {
      console.error('Error marking app as updated:', error);
    }
  }

  // Method to refresh server configuration
  async refreshServerConfig() {
    await this.loadServerConfig();
  }

  // Method to get current configuration
  getCurrentConfig() {
    return {
      latestVersion: this.latestVersion,
      checkInterval: this.checkInterval,
      playStoreUrl: this.playStoreUrl,
      configUrl: this.configUrl
    };
  }
}

// Export singleton instance
export default new SmartUpdateService();
